import { useState, type FormEvent } from 'react'
import { useServiceMutations } from '@/api/queries'
import type { ServiceInfo } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { NumberInput } from './NumberInput'
import { targetLabel, type Target } from './target'

const MIN = 1
const MAX = 1000

interface ScaleDialogProps {
  target: Target
  service: ServiceInfo
  onClose: () => void
}

export function ScaleDialog({ target, service, onClose }: ScaleDialogProps) {
  const { scale } = useServiceMutations()
  const toast = useToast()
  const [count, setCount] = useState(String(service.instanceCount))
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const value = Number(count)
    if (count.trim() === '' || !Number.isInteger(value) || value < MIN || value > MAX) {
      setError(`Enter a whole number between ${MIN} and ${MAX}.`)
      return
    }
    setError(null)
    try {
      await scale.mutateAsync({ ...target, service: service.name, count: value })
      toast.notify(`${service.name} scaled to ${value} instance${value === 1 ? '' : 's'}`, 'success')
      onClose()
    } catch (err) {
      toast.error(err, 'Scale failed')
    }
  }

  return (
    <Modal
      title={`Scale ${service.name}`}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button onClick={onClose} disabled={scale.isPending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="scale-form" loading={scale.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="scale-form" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Instances" htmlFor="scale-count" required error={error} hint={`Currently ${service.instanceCount} in ${targetLabel(target)}. Between ${MIN} and ${MAX}.`}>
          <NumberInput id="scale-count" min={MIN} max={MAX} value={count} onChange={setCount} />
        </Field>
      </form>
    </Modal>
  )
}
